import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./GuidelinesManagement.css";
import HighlightedTextarea from "../components/HighlightedTextarea";
import PromptStats from "../components/PromptStats";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function GuidelinesManagement() {
  const navigate = useNavigate();
  const [guidelines, setGuidelines] = useState([]);
  const [allGuidelines, setAllGuidelines] = useState([]); // Unfiltered for dashboard
  const [filteredGuidelines, setFilteredGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Filter states - multi-select arrays
  const [insurersFilter, setInsurersFilter] = useState([]);
  const [sourcesFilter, setSourcesFilter] = useState([]);
  const [cptCodesFilter, setCptCodesFilter] = useState([]);
  const [hasGeneratedFilter, setHasGeneratedFilter] = useState(''); // 'yes', 'no', or ''
  
  // CPB name search state (dropdown like file name)
  const [cpbSearchQuery, setCpbSearchQuery] = useState('');
  const [cpbFilter, setCpbFilter] = useState('');
  const [showCpbDropdown, setShowCpbDropdown] = useState(false);
  const cpbDropdownRef = useRef(null);

  // CPT Code search state (metadata)
  const [cptSearchQuery, setCptSearchQuery] = useState("");
  const [showCptDropdown, setShowCptDropdown] = useState(false);
  const cptDropdownRef = useRef(null);

  // Scenario CPT Code search state
  const [scenarioCptCodesFilter, setScenarioCptCodesFilter] = useState([]);
  const [scenarioCptSearchQuery, setScenarioCptSearchQuery] = useState("");
  const [showScenarioCptDropdown, setShowScenarioCptDropdown] = useState(false);
  const scenarioCptDropdownRef = useRef(null);

  // Generate Structured Guidelines states
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [llmProvider, setLlmProvider] = useState("gemini");
  const [processing, setProcessing] = useState(false);
  const [processMessage, setProcessMessage] = useState(null);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
  });
  const [cancelling, setCancelling] = useState(false);
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  const cancelRequestedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const availableVariables = [
    {
      name: "guideline_id",
      description: "The unique identifier for the guideline",
    },
    { name: "cpb", description: "Clinical Policy Bulletin title" },
    {
      name: "procedures",
      description: "JSON object mapping procedure codes to descriptions",
    },
    {
      name: "original_content",
      description: "JSON object with original guideline content",
    },
    {
      name: "referenced_guidelines",
      description: "JSON object containing referenced guideline data",
    },
  ];

  useEffect(() => {
    fetchGuidelines();

    // Refetch when tab regains focus to pick up data changes
    const handleFocus = () => fetchGuidelines();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cptDropdownRef.current && !cptDropdownRef.current.contains(event.target)) {
        setShowCptDropdown(false);
      }
      if (cpbDropdownRef.current && !cpbDropdownRef.current.contains(event.target)) {
        setShowCpbDropdown(false);
      }
      if (scenarioCptDropdownRef.current && !scenarioCptDropdownRef.current.contains(event.target)) {
        setShowScenarioCptDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...guidelines];

    // Multi-select insurers filter
    if (insurersFilter.length > 0) {
      filtered = filtered.filter((g) =>
        g.insurer && insurersFilter.includes(g.insurer)
      );
    }

    // CPB filter
    if (cpbFilter) {
      filtered = filtered.filter(
        (g) => g.cpb && g.cpb.toLowerCase().includes(cpbFilter.toLowerCase())
      );
    }

    // Multi-select CPT codes filter (metadata)
    if (cptCodesFilter.length > 0) {
      filtered = filtered.filter((g) => {
        if (!g.cpt_codes || !Array.isArray(g.cpt_codes)) return false;
        return cptCodesFilter.some((selectedCode) =>
          g.cpt_codes.some((code) => code.toLowerCase() === selectedCode.toLowerCase())
        );
      });
    }

    // Multi-select scenario CPT codes filter
    if (scenarioCptCodesFilter.length > 0) {
      filtered = filtered.filter((g) => {
        if (!g.scenario_cpt_codes || !Array.isArray(g.scenario_cpt_codes)) return false;
        return scenarioCptCodesFilter.some((selectedCode) =>
          g.scenario_cpt_codes.some((code) => code.toLowerCase() === selectedCode.toLowerCase())
        );
      });
    }

    // Multi-select sources filter
    if (sourcesFilter.length > 0) {
      filtered = filtered.filter((g) =>
        g.source && sourcesFilter.includes(g.source)
      );
    }

    // Generated filter
    if (hasGeneratedFilter === "yes") {
      filtered = filtered.filter((g) => g.has_llm_generated === true);
    } else if (hasGeneratedFilter === "no") {
      filtered = filtered.filter((g) => !g.has_llm_generated);
    }

    setFilteredGuidelines(filtered);
  }, [guidelines, insurersFilter, cpbFilter, cptCodesFilter, scenarioCptCodesFilter, sourcesFilter, hasGeneratedFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}/processed-guidelines-list?limit=10000`
      );
      setGuidelines(response.data);
      setAllGuidelines(response.data);
      setFilteredGuidelines(response.data);
    } catch (err) {
      console.error("Error fetching guidelines:", err);
      setError(err.response?.data?.detail || "Failed to load guidelines");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredGuidelines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGuidelines = useMemo(
    () => filteredGuidelines.slice(startIndex, endIndex),
    [filteredGuidelines, startIndex, endIndex]
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Get all unique values from ALL guidelines (not filtered) for dashboard
  const getAllUniqueInsurers = useMemo(() => {
    const insurerData = {};
    allGuidelines.forEach((g) => {
      const insurer = g.insurer || "Unknown";
      if (!insurerData[insurer]) {
        insurerData[insurer] = { count: 0, cptCodes: new Set() };
      }
      insurerData[insurer].count++;
      if (g.cpt_codes && Array.isArray(g.cpt_codes)) {
        g.cpt_codes.forEach(code => insurerData[insurer].cptCodes.add(code));
      }
    });
    return Object.entries(insurerData)
      .map(([name, data]) => ({
        name,
        count: data.count,
        cptCount: data.cptCodes.size
      }))
      .sort((a, b) => b.count - a.count);
  }, [allGuidelines]);

  // Get all unique sources from ALL guidelines
  const getAllUniqueSources = useMemo(() => {
    const sourceData = {};
    allGuidelines.forEach((g) => {
      const source = g.source || "Unknown";
      if (!sourceData[source]) {
        sourceData[source] = 0;
      }
      sourceData[source]++;
    });
    return Object.entries(sourceData)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [allGuidelines]);

  // Memoize all unique CPT codes (metadata)
  const allCptCodes = useMemo(() => {
    const values = new Set();
    guidelines.forEach((g) => {
      if (g.cpt_codes && Array.isArray(g.cpt_codes)) {
        g.cpt_codes.forEach((code) => values.add(code));
      }
    });
    return Array.from(values).sort();
  }, [guidelines]);

  // Get filtered CPT codes based on search query (metadata)
  const getFilteredCptCodes = useMemo(() => {
    if (!cptSearchQuery) return allCptCodes;
    return allCptCodes.filter((code) =>
      code.toLowerCase().includes(cptSearchQuery.toLowerCase())
    );
  }, [allCptCodes, cptSearchQuery]);

  // Memoize all unique scenario CPT codes
  const allScenarioCptCodes = useMemo(() => {
    const values = new Set();
    guidelines.forEach((g) => {
      if (g.scenario_cpt_codes && Array.isArray(g.scenario_cpt_codes)) {
        g.scenario_cpt_codes.forEach((code) => values.add(code));
      }
    });
    return Array.from(values).sort();
  }, [guidelines]);

  // Get filtered scenario CPT codes based on search query
  const getFilteredScenarioCptCodes = useMemo(() => {
    if (!scenarioCptSearchQuery) return allScenarioCptCodes;
    return allScenarioCptCodes.filter((code) =>
      code.toLowerCase().includes(scenarioCptSearchQuery.toLowerCase())
    );
  }, [allScenarioCptCodes, scenarioCptSearchQuery]);

  // Get unique CPB names for dropdown
  const allCpbNames = useMemo(() => {
    const values = new Set();
    guidelines.forEach((g) => {
      if (g.cpb) values.add(g.cpb);
    });
    return Array.from(values).sort();
  }, [guidelines]);

  // Filtered CPB names based on search
  const getFilteredCpbNames = useMemo(() => {
    if (!cpbSearchQuery) return allCpbNames.slice(0, 20);
    return allCpbNames.filter((cpb) =>
      cpb.toLowerCase().includes(cpbSearchQuery.toLowerCase())
    ).slice(0, 20);
  }, [allCpbNames, cpbSearchQuery]);

  // Dashboard metrics based on FILTERED guidelines (for stats)
  const dashboardMetrics = useMemo(() => {
    const totalGuidelines = filteredGuidelines.length;
    const generatedCount = filteredGuidelines.filter(g => g.has_llm_generated).length;
    const notGeneratedCount = totalGuidelines - generatedCount;
    const generatedPercent = totalGuidelines > 0 ? Math.round((generatedCount / totalGuidelines) * 100) : 0;
    
    // Unique CPT codes in filtered results
    const cptCodesSet = new Set();
    filteredGuidelines.forEach(g => {
      if (g.cpt_codes && Array.isArray(g.cpt_codes)) {
        g.cpt_codes.forEach(code => cptCodesSet.add(code));
      }
    });
    const uniqueCptCodes = cptCodesSet.size;
    
    return {
      totalGuidelines,
      generatedCount,
      notGeneratedCount,
      generatedPercent,
      uniqueCptCodes,
    };
  }, [filteredGuidelines]);

  // Dashboard click handlers - toggle filters
  const handleDashboardClick = (filterType, value) => {
    setCurrentPage(1);
    
    switch (filterType) {
      case 'total':
        // Clear all filters
        setInsurersFilter([]);
        setSourcesFilter([]);
        setCptCodesFilter([]);
        setScenarioCptCodesFilter([]);
        setHasGeneratedFilter('');
        setCpbFilter('');
        setCpbSearchQuery('');
        break;
      case 'generated':
        setHasGeneratedFilter(prev => prev === value ? '' : value);
        break;
      case 'insurer':
        // Toggle insurer in multi-select array
        setInsurersFilter(prev => {
          if (prev.includes(value)) {
            return prev.filter(i => i !== value);
          } else {
            return [...prev, value];
          }
        });
        break;
      case 'source':
        // Toggle source in multi-select array
        setSourcesFilter(prev => {
          if (prev.includes(value)) {
            return prev.filter(s => s !== value);
          } else {
            return [...prev, value];
          }
        });
        break;
      default:
        break;
    }
  };

  // Check if a dashboard filter is active
  const isFilterActive = (filterType, value) => {
    switch (filterType) {
      case 'generated':
        return hasGeneratedFilter === value;
      case 'insurer':
        return insurersFilter.includes(value);
      case 'source':
        return sourcesFilter.includes(value);
      default:
        return false;
    }
  };

  // Has any active filters
  const hasActiveFilters = insurersFilter.length > 0 || sourcesFilter.length > 0 ||
    cptCodesFilter.length > 0 || scenarioCptCodesFilter.length > 0 || hasGeneratedFilter || cpbFilter;

  // Handle CPT code selection
  const handleCptCodeToggle = (code) => {
    setCptCodesFilter(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
    setCptSearchQuery('');
    setCurrentPage(1);
  };

  // Remove a selected CPT code
  const handleRemoveCptCode = (code) => {
    setCptCodesFilter(prev => prev.filter(c => c !== code));
    setCurrentPage(1);
  };

  // Handle scenario CPT code selection
  const handleScenarioCptCodeToggle = (code) => {
    setScenarioCptCodesFilter(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
    setScenarioCptSearchQuery('');
    setCurrentPage(1);
  };

  // Remove a selected scenario CPT code
  const handleRemoveScenarioCptCode = (code) => {
    setScenarioCptCodesFilter(prev => prev.filter(c => c !== code));
    setCurrentPage(1);
  };

  // CPB name selection
  const handleCpbSelect = (cpb) => {
    setCpbSearchQuery('');
    setCpbFilter(cpb);
    setShowCpbDropdown(false);
    setCurrentPage(1);
  };

  const handleClearCpb = () => {
    setCpbSearchQuery('');
    setCpbFilter('');
    setShowCpbDropdown(false);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setInsurersFilter([]);
    setSourcesFilter([]);
    setCptCodesFilter([]);
    setScenarioCptCodesFilter([]);
    setHasGeneratedFilter('');
    setCpbFilter('');
    setCpbSearchQuery('');
    setCptSearchQuery('');
    setScenarioCptSearchQuery('');
    setCurrentPage(1);
  };

  const handleCancelProcessing = () => {
    cancelRequestedRef.current = true;
    setCancelling(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleGenerateAllStructuredGuidelines = async () => {
    if (filteredGuidelines.length === 0) {
      setProcessMessage({ type: "error", text: "No guidelines to process" });
      return;
    }

    try {
      setProcessing(true);
      setProcessMessage(null);
      cancelRequestedRef.current = false;
      setCancelling(false);

      const total = filteredGuidelines.length;
      let processedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;

      setProgress({ current: 0, total, percentage: 0 });

      for (let i = 0; i < filteredGuidelines.length; i++) {
        if (cancelRequestedRef.current) {
          setProcessMessage({
            type: "warning",
            text: `Operation cancelled. Processed: ${processedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}. Remaining: ${
              total - i
            } guidelines.`,
          });
          break;
        }

        const guideline = filteredGuidelines[i];
        const current = i + 1;
        const percentage = Math.round((current / total) * 100);

        setProgress({ current, total, percentage });

        abortControllerRef.current = new AbortController();

        try {
          const response = await axios.post(
            `${API_BASE_URL}/llm-generated-guidelines/process-guideline/${encodeURIComponent(
              guideline.guideline_id
            )}`,
            {
              prompt: customPrompt || null,
              llm_provider: llmProvider,
              llm_model: null,
              llm_version: null,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
              timeout: 1800000,
              signal: abortControllerRef.current.signal,
            }
          );

          if (response.data && response.data.guideline_id) {
            processedCount++;
          } else {
            skippedCount++;
          }
        } catch (err) {
          if (
            axios.isCancel(err) ||
            err.name === "CanceledError" ||
            err.code === "ERR_CANCELED"
          ) {
            console.log(
              `Request for guideline ${guideline.guideline_id} was cancelled`
            );
            break;
          }
          console.error(
            `Error processing guideline ${guideline.guideline_id}:`,
            err
          );
          errorCount++;
        }
      }

      abortControllerRef.current = null;

      setProgress({ current: total, total, percentage: 100 });

      if (!cancelRequestedRef.current) {
        setProcessMessage({
          type: "success",
          text: `Generation complete! Processed: ${processedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}`,
        });
      }

      setShowPromptDialog(false);
      setCustomPrompt("");
      setLlmProvider("gemini");

      await fetchGuidelines();

      setTimeout(() => {
        setProcessMessage(null);
        setProgress({ current: 0, total: 0, percentage: 0 });
      }, 10000);
    } catch (err) {
      console.error("Error processing guidelines:", err);
      setProcessMessage({
        type: "error",
        text: `Error: ${err.message}`,
      });
    } finally {
      setProcessing(false);
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="guidelines-loading">
        <div className="spinner"></div>
        <p>Loading guidelines...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guidelines-error">
        <span className="error-icon">❌</span>
        <span>{error}</span>
        <button onClick={fetchGuidelines} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="guidelines-management-page">
      {processMessage && (
        <div className={`process-message ${processMessage.type}`}>
          <span className="message-icon">
            {processMessage.type === "success"
              ? "✅"
              : processMessage.type === "error"
              ? "❌"
              : "⚠️"}
          </span>
          <span>{processMessage.text}</span>
        </div>
      )}

      <div className="guidelines-header">
        <h2>Guidelines Management</h2>
        <div className="header-buttons">
          {hasActiveFilters && (
            <button onClick={handleClearAllFilters} className="clear-all-filters-btn">
              Clear All Filters
            </button>
          )}
          <button
            onClick={() => setShowPromptDialog(true)}
            className="generate-structured-button"
            disabled={filteredGuidelines.length === 0}
          >
            Generate Structured Guidelines ({filteredGuidelines.length})
          </button>
          <button onClick={fetchGuidelines} className="refresh-button">
            Refresh
          </button>
        </div>
      </div>

      {/* Dashboard Section */}
      <div className="guidelines-dashboard">
        {/* Stats Row */}
        <div className="dashboard-row stats-row">
          <button
            className={`stat-card ${!hasActiveFilters ? 'active' : ''}`}
            onClick={() => handleDashboardClick('total', null)}
          >
            <span className="stat-value">{dashboardMetrics.totalGuidelines.toLocaleString()}</span>
            <span className="stat-label">Total Guidelines</span>
          </button>
          
          <button
            className={`stat-card green ${isFilterActive('generated', 'yes') ? 'active' : ''}`}
            onClick={() => handleDashboardClick('generated', 'yes')}
          >
            <span className="stat-value">{dashboardMetrics.generatedCount.toLocaleString()}</span>
            <span className="stat-label">Structured</span>
            <span className="stat-percent">{dashboardMetrics.generatedPercent}%</span>
          </button>
          
          <button
            className={`stat-card red ${isFilterActive('generated', 'no') ? 'active' : ''}`}
            onClick={() => handleDashboardClick('generated', 'no')}
          >
            <span className="stat-value">{dashboardMetrics.notGeneratedCount.toLocaleString()}</span>
            <span className="stat-label">Pending</span>
            <span className="stat-percent">{100 - dashboardMetrics.generatedPercent}%</span>
          </button>
          
          <div className="stat-card info">
            <span className="stat-value">{dashboardMetrics.uniqueCptCodes.toLocaleString()}</span>
            <span className="stat-label">CPT Codes</span>
          </div>
        </div>

        {/* Source Breakdown Row */}
        <div className="dashboard-row breakdown-row">
          <div className="breakdown-group full-width">
            <span className="breakdown-label">Source {sourcesFilter.length > 0 && `(${sourcesFilter.length} selected)`}</span>
            <div className="breakdown-items scrollable">
              {getAllUniqueSources.map((source) => (
                <button
                  key={source.name}
                  className={`breakdown-item ${isFilterActive('source', source.name) ? 'active' : ''}`}
                  onClick={() => handleDashboardClick('source', source.name)}
                >
                  {source.name} <span className="item-count">{source.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Insurer Breakdown Row */}
        <div className="dashboard-row insurer-row">
          <div className="breakdown-group full-width">
            <span className="breakdown-label">Insurer {insurersFilter.length > 0 && `(${insurersFilter.length} selected)`}</span>
            <div className="breakdown-items scrollable">
              {getAllUniqueInsurers.map((insurer) => (
                <button
                  key={insurer.name}
                  className={`breakdown-item ${isFilterActive('insurer', insurer.name) ? 'active' : ''}`}
                  onClick={() => handleDashboardClick('insurer', insurer.name)}
                >
                  {insurer.name} <span className="item-count">{insurer.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Search Filters Row - Outside Dashboard */}
      <div className="search-filters-row">
        {/* CPB Name Search with Dropdown */}
        <div className="search-container" ref={cpbDropdownRef}>
          <label className="search-label">CPB Name</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search CPB names..."
              value={cpbSearchQuery}
              onChange={(e) => {
                setCpbSearchQuery(e.target.value);
                setShowCpbDropdown(true);
              }}
              onFocus={() => setShowCpbDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCpbFilter(cpbSearchQuery);
                  setShowCpbDropdown(false);
                  setCurrentPage(1);
                }
              }}
            />
            {cpbFilter && (
              <button className="search-clear-btn" onClick={handleClearCpb}>×</button>
            )}
          </div>
          
          {cpbFilter && (
            <div className="selected-tag-container">
              <span className="selected-tag">
                {cpbFilter.length > 30 ? cpbFilter.slice(0, 30) + '...' : cpbFilter}
                <button className="tag-remove" onClick={handleClearCpb}>×</button>
              </span>
            </div>
          )}
          
          {showCpbDropdown && cpbSearchQuery.length >= 2 && (
            <div className="search-dropdown">
              {getFilteredCpbNames.length > 0 ? (
                getFilteredCpbNames.map((cpb) => (
                  <div
                    key={cpb}
                    className={`dropdown-item ${cpbFilter === cpb ? 'selected' : ''}`}
                    onClick={() => handleCpbSelect(cpb)}
                  >
                    <span className="dropdown-item-text">{cpb.length > 60 ? cpb.slice(0, 60) + '...' : cpb}</span>
                    {cpbFilter === cpb && <span className="dropdown-check">✓</span>}
                  </div>
                ))
              ) : (
                <div className="dropdown-empty">No CPB names found</div>
              )}
            </div>
          )}
        </div>

        {/* CPT Code Multi-Select (Metadata) */}
        <div className="search-container" ref={cptDropdownRef}>
          <label className="search-label">CPT Codes (Metadata)</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search CPT codes..."
              value={cptSearchQuery}
              onChange={(e) => {
                setCptSearchQuery(e.target.value);
                setShowCptDropdown(true);
              }}
              onFocus={() => setShowCptDropdown(true)}
            />
            {cptCodesFilter.length > 0 && (
              <button className="search-clear-btn" onClick={() => setCptCodesFilter([])}>×</button>
            )}
          </div>
          
          {cptCodesFilter.length > 0 && (
            <div className="selected-tag-container">
              {cptCodesFilter.map(code => (
                <span key={code} className="selected-tag">
                  {code}
                  <button className="tag-remove" onClick={() => handleRemoveCptCode(code)}>×</button>
                </span>
              ))}
            </div>
          )}
          
          {showCptDropdown && (cptSearchQuery.length >= 2 || getFilteredCptCodes.length > 0) && (
            <div className="search-dropdown">
              {getFilteredCptCodes.length > 0 ? (
                getFilteredCptCodes.slice(0, 50).map((code) => (
                  <div
                    key={code}
                    className={`dropdown-item ${cptCodesFilter.includes(code) ? 'selected' : ''}`}
                    onClick={() => handleCptCodeToggle(code)}
                  >
                    <span className="dropdown-item-text">{code}</span>
                    {cptCodesFilter.includes(code) && <span className="dropdown-check">✓</span>}
                  </div>
                ))
              ) : cptSearchQuery.length >= 2 ? (
                <div className="dropdown-empty">No CPT codes found</div>
              ) : null}
              {getFilteredCptCodes.length > 50 && (
                <div className="dropdown-hint">+{getFilteredCptCodes.length - 50} more. Type to narrow results.</div>
              )}
            </div>
          )}
        </div>

        {/* Scenario CPT Code Multi-Select */}
        <div className="search-container" ref={scenarioCptDropdownRef}>
          <label className="search-label">CPT Codes (Scenarios)</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search scenario CPT codes..."
              value={scenarioCptSearchQuery}
              onChange={(e) => {
                setScenarioCptSearchQuery(e.target.value);
                setShowScenarioCptDropdown(true);
              }}
              onFocus={() => setShowScenarioCptDropdown(true)}
            />
            {scenarioCptCodesFilter.length > 0 && (
              <button className="search-clear-btn" onClick={() => setScenarioCptCodesFilter([])}>×</button>
            )}
          </div>

          {scenarioCptCodesFilter.length > 0 && (
            <div className="selected-tag-container">
              {scenarioCptCodesFilter.map(code => (
                <span key={code} className="selected-tag">
                  {code}
                  <button className="tag-remove" onClick={() => handleRemoveScenarioCptCode(code)}>×</button>
                </span>
              ))}
            </div>
          )}

          {showScenarioCptDropdown && (scenarioCptSearchQuery.length >= 2 || getFilteredScenarioCptCodes.length > 0) && (
            <div className="search-dropdown">
              {getFilteredScenarioCptCodes.length > 0 ? (
                getFilteredScenarioCptCodes.slice(0, 50).map((code) => (
                  <div
                    key={code}
                    className={`dropdown-item ${scenarioCptCodesFilter.includes(code) ? 'selected' : ''}`}
                    onClick={() => handleScenarioCptCodeToggle(code)}
                  >
                    <span className="dropdown-item-text">{code}</span>
                    {scenarioCptCodesFilter.includes(code) && <span className="dropdown-check">✓</span>}
                  </div>
                ))
              ) : scenarioCptSearchQuery.length >= 2 ? (
                <div className="dropdown-empty">No scenario CPT codes found</div>
              ) : null}
              {getFilteredScenarioCptCodes.length > 50 && (
                <div className="dropdown-hint">+{getFilteredScenarioCptCodes.length - 50} more. Type to narrow results.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Prompt Dialog */}
      {showPromptDialog && (
        <div
          className="prompt-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !processing) {
              setShowPromptDialog(false);
              setCustomPrompt("");
              setLlmProvider("gemini");
              setProgress({ current: 0, total: 0, percentage: 0 });
            }
          }}
        >
          <div className="prompt-dialog">
            <h3>
              Generate Structured Guidelines for {filteredGuidelines.length}{" "}
              Guideline(s)
            </h3>
            <p>
              This will run LLM processing on each filtered guideline to
              generate structured output.
            </p>

            {/* LLM Provider Selection */}
            <div className="llm-provider-section">
              <label className="provider-label">LLM Provider:</label>
              <select
                value={llmProvider}
                onChange={(e) => setLlmProvider(e.target.value)}
                className="provider-select"
                disabled={processing}
              >
                <option value="openai">OpenAI GPT-5</option>
                <option value="gemini">Google Gemini 3.0</option>
              </select>
            </div>

            {/* Default Prompt Dropdown */}
            <div className="default-prompt-section">
              <button
                type="button"
                onClick={async () => {
                  if (!showDefaultPrompt && !defaultPrompt) {
                    setLoadingDefaultPrompt(true);
                    try {
                      const response = await axios.get(
                        `${API_BASE_URL}/llm-generated-guidelines/default-prompt`
                      );
                      setDefaultPrompt(response.data);
                    } catch (err) {
                      console.error("Error loading default prompt:", err);
                    } finally {
                      setLoadingDefaultPrompt(false);
                    }
                  }
                  setShowDefaultPrompt(!showDefaultPrompt);
                }}
                className="default-prompt-toggle"
              >
                {showDefaultPrompt ? "▼" : "▶"}{" "}
                {loadingDefaultPrompt ? "Loading..." : "Show Default Prompt"}
              </button>
              {showDefaultPrompt && defaultPrompt && (
                <div className="default-prompt-content">{defaultPrompt}</div>
              )}
              {showDefaultPrompt && defaultPrompt && (
                <PromptStats
                  text={defaultPrompt}
                  knownVariables={['guideline_id', 'insurer', 'cpb', 'source', 'cpt_codes', 'hcpcs_codes', 'procedures', 'original_content', 'referenced_guidelines', 'ref_content', 'ref_procedures', 'link', 'effective_date', 'created_at']}
                  label="Default Prompt"
                  estimateEndpoint="/llm-generated-guidelines/estimate-prompt-tokens"
                />
              )}
            </div>

            {/* Custom Prompt */}
            <HighlightedTextarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter custom prompt here, or leave empty to use default prompt..."
              disabled={processing}
              variables={availableVariables.map((v) => v.name)}
            />
            {customPrompt && (
              <PromptStats
                text={customPrompt}
                knownVariables={['guideline_id', 'insurer', 'cpb', 'source', 'cpt_codes', 'hcpcs_codes', 'procedures', 'original_content', 'referenced_guidelines', 'ref_content', 'ref_procedures', 'link', 'effective_date', 'created_at']}
                label="Custom Prompt"
                estimateEndpoint="/llm-generated-guidelines/estimate-prompt-tokens"
              />
            )}

            {/* Available Variables */}
            <div className="variables-section">
              <h4>Available Variables:</h4>
              <p className="variables-hint">
                Use curly braces to insert variables: {"{variable_name}"}
              </p>
              <div className="variables-list">
                {availableVariables.map((v, idx) => (
                  <div key={idx} className="variable-item">
                    <code>{`{${v.name}}`}</code>
                    <span>{v.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            {processing && progress.total > 0 && (
              <div className="progress-container">
                <div className="progress-header">
                  <span
                    className={`progress-text ${
                      cancelling ? "cancelling-text" : ""
                    }`}
                  >
                    {cancelling
                      ? `Cancelling...`
                      : `Processing guideline ${progress.current} of ${progress.total} (${progress.percentage}%)`}
                  </span>
                </div>
                <div
                  className={`progress-bar-wrapper ${
                    cancelling ? "cancelling" : ""
                  }`}
                >
                  <div
                    className={`progress-bar-fill ${
                      cancelling ? "cancelling" : ""
                    }`}
                    style={{ width: `${progress.percentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="dialog-buttons">
              {processing ? (
                <button
                  onClick={handleCancelProcessing}
                  className={`cancel-processing-button ${
                    cancelling ? "cancelling" : ""
                  }`}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Cancel Operation"}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleGenerateAllStructuredGuidelines}
                    disabled={processing || filteredGuidelines.length === 0}
                    className="confirm-button"
                  >
                    Generate for {filteredGuidelines.length} Guideline(s)
                  </button>
                  <button
                    onClick={() => {
                      setShowPromptDialog(false);
                      setCustomPrompt("");
                      setLlmProvider("gemini");
                    }}
                    disabled={processing}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {guidelines.length === 0 ? (
        <div className="no-guidelines">
          <p>No processed guidelines found.</p>
        </div>
      ) : filteredGuidelines.length === 0 ? (
        <div className="no-guidelines">
          <p>No guidelines match the current filters.</p>
        </div>
      ) : (
        <>
          {/* Table info */}
          <div className="table-info">
            <span>
              Showing {startIndex + 1}-
              {Math.min(endIndex, filteredGuidelines.length)} of{" "}
              {filteredGuidelines.length} guidelines
            </span>
            <div className="items-per-page">
              <label htmlFor="itemsPerPage">Per page:</label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="guidelines-table-container">
            <div className="guidelines-table">
              <table>
                <thead>
                  <tr>
                    <th>CPB Title</th>
                    <th>Insurer</th>
                    <th>Source</th>
                    <th>CPT Codes</th>
                    <th>Generated</th>
                    <th>Created</th>
                    <th>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGuidelines.map((guideline) => (
                    <tr
                      key={guideline.guideline_id}
                      className="clickable-row"
                      onClick={(e) => {
                        const url = `/guideline_view/${encodeURIComponent(
                          guideline.guideline_id
                        )}`;
                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 1) {
                          e.preventDefault();
                          window.open(
                            `/guideline_view/${encodeURIComponent(
                              guideline.guideline_id
                            )}`,
                            "_blank"
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="cpb-cell" title={guideline.cpb || "N/A"}>
                        {guideline.cpb || "N/A"}
                      </td>
                      <td>{guideline.insurer || "N/A"}</td>
                      <td>{guideline.source || "N/A"}</td>
                      <td className="cpt-codes-cell">
                        {guideline.cpt_codes &&
                        guideline.cpt_codes.length > 0 ? (
                          <span title={guideline.cpt_codes.join(", ")}>
                            {guideline.cpt_codes.slice(0, 3).join(", ")}
                            {guideline.cpt_codes.length > 3 &&
                              ` +${guideline.cpt_codes.length - 3}`}
                          </span>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="generated-status-cell">
                        {guideline.has_llm_generated ? (
                          <span
                            className="generated-badge has-generated"
                            title="LLM generated guideline exists"
                          >
                            <span className="badge-icon">✓</span>
                            <span className="badge-text">Yes</span>
                          </span>
                        ) : (
                          <span
                            className="generated-badge no-generated"
                            title="No LLM generated guideline"
                          >
                            <span className="badge-icon">✗</span>
                            <span className="badge-text">No</span>
                          </span>
                        )}
                      </td>
                      <td className="date-cell">
                        {formatDate(guideline.created_at)}
                      </td>
                      <td
                        className="link-cell"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {guideline.link ? (
                          <a
                            href={guideline.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="source-link"
                            title="Open source"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M15 3h6v6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M10 14L21 3"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </a>
                        ) : (
                          <span className="no-link">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                ‹
              </button>

              {getPageNumbers().map((page, index) =>
                page === "..." ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="pagination-ellipsis"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`pagination-button ${
                      currentPage === page ? "active" : ""
                    }`}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                »
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GuidelinesManagement;
